/**
 * useUserDoSync — thin React wrapper around the unified `useDoSync`
 * engine. Owns user-scope React state (`syncStatus`, `activityData`,
 * `hasCompletedInitialSync`, online flag) and delegates everything
 * else to the engine + UserDoAdapter.
 *
 * Pre-unification this hook was 310 LOC of message routing,
 * subscription wiring, and protocol decoding. All of that now lives in
 * the engine (`use-do-sync.ts`) and the adapter (`user-do-adapter.ts`).
 */

import type {
  ActivitySummary,
  SyncClientMessage,
} from '@flashka/shared/sync-engine/schema/sync-types';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import {
  type AccountSeedEvent,
  type AccountSeedState,
  INITIAL_ACCOUNT_SEED_STATE,
  nextAccountSeedState,
} from '../../account-seed';
import { getSyncStorage } from '../../provider/sync-storage';
import { useUserDoConnection } from '../../provider/user-do-connection';
import { useDoSync } from '../engine/use-do-sync';
import {
  createUserDoAdapter,
  type LegacyMigrationClient,
} from '../engine/user-do-adapter';
import { getOnlineDetector } from '../online-detector';
import { perfLog, perfNow } from '../perf-log'; // [perf-probe]
import { setPersonalSyncReady, setPersonalSyncStatus } from '../personal-sync-ready';
import { useFlashkaStore } from '../store/flashka-store';
import { getUnsyncedDeckTracker } from '../sync-bookkeeping/unsynced-decks';
import type { SyncCtx, SyncStatus } from './sync-context-types';

export type { LegacyMigrationClient } from '../engine/user-do-adapter';
export type { SyncMessageLogEntry } from '../message-log';
export type { SyncStatus } from './sync-context-types';

// ---------------------------------------------------------------------------
// Online detection
// ---------------------------------------------------------------------------

const subscribeOnline = (cb: () => void) => getOnlineDetector().subscribe(cb);
const getOnlineSnapshot = () => getOnlineDetector().getSnapshot();
const getOnlineServerSnapshot = () => getOnlineDetector().getServerSnapshot();

export function useIsOnline(): boolean {
  return useSyncExternalStore(
    subscribeOnline,
    getOnlineSnapshot,
    getOnlineServerSnapshot,
  );
}

// ---------------------------------------------------------------------------
// Server-synced timestamps — persisted in IndexedDB settings
// ---------------------------------------------------------------------------

// Exported for unit tests (see use-user-do-sync.spec.ts). Not part of the
// public sync-client surface — treat as module-internal.
export const SYNC_TS_KEY = 'doSyncTimestamps';
export const SYNC_VERSION_KEY = 'doSyncVersion';

export async function loadServerSyncedTs(): Promise<Record<string, string>> {
  const entry = await getSyncStorage().getByKey<{ key: string; value: unknown }>(
    'settings',
    SYNC_TS_KEY,
  );
  const value = entry?.value;
  // The map is persisted as a pre-serialized JSON string (see
  // `saveServerSyncedTs`). Older installs persisted the raw object; accept
  // both so an in-place upgrade doesn't drop the timestamps and force a full
  // re-sync.
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as Record<string, string>;
    } catch {
      return {};
    }
  }
  return (value as Record<string, string>) ?? {};
}

/**
 * Longest the persisted timestamp map may lag the in-memory one.
 *
 * Deliberately a THROTTLE (the timer is not restarted by later calls), not a
 * plain trailing debounce: a long burst still persists every interval instead
 * of holding everything until it ends, so the staleness window stays bounded
 * however many frames arrive.
 */
const SAVE_TS_INTERVAL_MS = 250;

let pendingTs: Record<string, string> | null = null;
let pendingTsTimer: ReturnType<typeof setTimeout> | null = null;

function writeServerSyncedTs(ts: Record<string, string>) {
  // Persist the (potentially 40k+ entry) timestamp map as a single
  // JSON string rather than a plain object. The RN storage layer
  // msgpack-encodes settings blobs, and msgpackr walks every entry of a
  // large object on the JS thread — ~1.5s per write on big libraries,
  // which froze the study screen on every card grade. A pre-stringified
  // value (native `JSON.stringify`, ~10ms) is written as one opaque
  // string blob (~10ms to encode), eliminating the freeze. `loadServerSyncedTs`
  // parses it back.
  const perfT0 = perfNow(); // [perf-probe]
  const json = JSON.stringify(ts);
  getSyncStorage().put('settings', { key: SYNC_TS_KEY, value: json });
  perfLog('save-server-ts stringify+put', perfT0, () => ({
    entries: Object.keys(ts).length,
    bytes: json.length,
  }));
}

/**
 * Record that the server-synced timestamps changed. COALESCED — see below.
 *
 * The map holds ONE ENTRY PER CARD IN THE WHOLE LIBRARY, not per deck, and
 * every caller passes the same live ref. Writing it per call meant rebuilding
 * the entire library's map for each 100-row bulk frame: opening a cold
 * 1,800-card deck on a 20k-card library rebuilt and re-encoded ~2.3 MB
 * eighteen times — roughly 20x write amplification against ~1.8 MB of real
 * card data, all of it `JSON.stringify` + msgpack on the JS thread, landing on
 * the frames the deck screen is trying to mount in. The probe comment that
 * used to live here said as much: "watch for this piling up during a cold-deck
 * rehydrate (19 frames on a 1.8k deck)."
 *
 * Coalescing is safe because this map is a CACHE, not the record: the card rows
 * themselves are persisted per frame by `storage.put`, and the commit version
 * is deliberately written last. A timestamp write lost to a crash costs a
 * re-diff of those cards on the next connect — an idempotent re-apply the
 * engine already performs — never data.
 *
 * Two rules that come with it, both load-bearing:
 * - {@link flushServerSyncedTsNow} runs before the version is written, so the
 *   version can never vouch for timestamps still sitting in memory.
 * - {@link cancelPendingServerSyncedTs} — NOT a flush — on wipe/user change. A
 *   pending write landing after a reset would re-plant a stale map over an
 *   empty library, and the client would then tell the server it is caught up.
 */
export async function saveServerSyncedTs(ts: Record<string, string>) {
  pendingTs = ts;
  if (pendingTsTimer != null) return;
  pendingTsTimer = setTimeout(() => {
    pendingTsTimer = null;
    const due = pendingTs;
    pendingTs = null;
    if (due) writeServerSyncedTs(due);
  }, SAVE_TS_INTERVAL_MS);
}

/** Persist any coalesced timestamp write immediately. */
export function flushServerSyncedTsNow() {
  if (pendingTsTimer != null) {
    clearTimeout(pendingTsTimer);
    pendingTsTimer = null;
  }
  const due = pendingTs;
  pendingTs = null;
  if (due) writeServerSyncedTs(due);
}

/** Drop a coalesced write without persisting it — for wipes and user changes. */
export function cancelPendingServerSyncedTs() {
  if (pendingTsTimer != null) {
    clearTimeout(pendingTsTimer);
    pendingTsTimer = null;
  }
  pendingTs = null;
}

// The commit version lives in the SAME `settings` table as the timestamps and
// (on web) the same IndexedDB DB as the card data, so it can't outlive the
// data it vouches for. On native it must stay in the SQLite-of-record settings
// (NOT the MMKV write-through cache) for the same reason — see the native
// hybrid-sync-storage notes; do not add `doSyncVersion` to the MMKV whitelist.
export async function loadServerVersion(): Promise<number | null> {
  const entry = await getSyncStorage().getByKey<{ key: string; value: unknown }>(
    'settings',
    SYNC_VERSION_KEY,
  );
  return typeof entry?.value === 'number' ? entry.value : null;
}

export async function saveServerVersion(version: number) {
  // The version is written LAST and vouches for everything before it, so any
  // coalesced timestamp write has to land first. Enforced HERE rather than at
  // each of the three adapter call sites, so the invariant cannot be missed by
  // a fourth one added later.
  flushServerSyncedTsNow();
  getSyncStorage().put('settings', { key: SYNC_VERSION_KEY, value: version });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export type UserDoSyncResult = {
  /** The socket / sync handshake only — see `SyncStatus`. */
  syncStatus: SyncStatus;
  /**
   * Bringing a legacy account over on first sign-in (`account-seed.ts`).
   * Separate from `syncStatus` on purpose: the socket is fine while it runs.
   */
  seed: AccountSeedState;
  isOnline: boolean;
  activityData: ActivitySummary | null;
  /**
   * True once `syncStatus` has reached `"ready"` at least once since
   * sign-in. Use this (not `syncStatus === "ready"`) to decide whether
   * the store reflects server truth — reconnects cycle back through
   * connecting/syncing but we don't want UI gates to flicker on every
   * reconnect.
   */
  hasCompletedInitialSync: boolean;
};

export function useUserDoSync(
  userId: string | null | undefined,
  legacyClient?: LegacyMigrationClient,
): UserDoSyncResult {
  const provider = useUserDoConnection();
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('disconnected');
  const [activityData, setActivityData] = useState<ActivitySummary | null>(null);
  const [hasCompletedInitialSync, setHasCompletedInitialSync] = useState(false);
  const [seed, setSeed] = useState<AccountSeedState>(INITIAL_ACCOUNT_SEED_STATE);
  const onSeedEvent = useCallback((event: AccountSeedEvent) => {
    setSeed((prev) => nextAccountSeedState(prev, event));
  }, []);
  const isOnline = useIsOnline();

  // Flip on the first `ready` we see (post sync-diff handshake); stays
  // true through reconnects so UI gates don't flicker.
  useEffect(() => {
    if (syncStatus === 'ready') setHasCompletedInitialSync(true);
  }, [syncStatus]);

  // Reset on actual identity changes — sign-out, sign-in, or a switch
  // to a different account. Keyed on the store's `currentUserId` (which
  // only changes via `hydrate()` / `reset()`), NOT on this hook's
  // `userId` argument, because the SyncProvider may flip the effective
  // userId null↔user on visibility-grace teardowns without the signed-in
  // identity actually changing. Resetting on those churns made the
  // dashboard "wait for first sync before deciding new-vs-returning
  // user" gate fire on every reconnect; once truly true for a session,
  // a transient WebSocket drop doesn't invalidate the fact that the
  // Zustand store reflects server truth.
  const currentUserId = useFlashkaStore((s) => s.currentUserId);
  const lastSeenUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    const next = currentUserId ?? null;
    if (lastSeenUserIdRef.current === next) return;
    lastSeenUserIdRef.current = next;
    setHasCompletedInitialSync(false);
    onSeedEvent({ type: 'reset' });
  }, [currentUserId, onSeedEvent]);

  const serverTsRef = useRef<Record<string, string>>({});
  const serverVersionRef = useRef<number | null>(null);
  const serverTsUserRef = useRef<string | null>(null);
  const serverTsLoadedRef = useRef<Promise<void>>(Promise.resolve());
  const syncStatusRef = useRef<SyncStatus>('disconnected');
  syncStatusRef.current = syncStatus;
  const hasCompletedInitialSyncRef = useRef(false);

  // The engine writes through ctx.sendRef.current. We allocate the ref
  // here; the adapter rewires it on each connection cycle (via the
  // engine's `send` callback in onProtocolMessage / subscribeDirty).
  // `false` until the engine binds: nothing is delivered before a socket
  // exists, and a caller that retires state on a send must see that.
  const sendRef = useRef<(msg: SyncClientMessage) => boolean>(() => false);
  const sendCompressedRef = useRef<(msg: SyncClientMessage) => void>(() => {});

  // Load the persisted server-ts map on user change. Catch failures so
  // performSyncInit's await never deadlocks — a failed load just sends
  // an empty timestamp map and the server responds with a full diff.
  useEffect(() => {
    if (!userId) return;
    if (serverTsUserRef.current !== userId) {
      // CANCEL, never flush: a coalesced write from the previous user must not
      // land on top of the map we are about to load for this one.
      cancelPendingServerSyncedTs();
      serverTsRef.current = {};
      serverVersionRef.current = null;
      serverTsUserRef.current = userId;
    }
    serverTsLoadedRef.current = Promise.all([loadServerSyncedTs(), loadServerVersion()])
      .then(([ts, version]) => {
        serverTsRef.current = { ...ts, ...serverTsRef.current };
        // Don't clobber a version set by a concurrent in-flight sync-diff.
        if (serverVersionRef.current === null) serverVersionRef.current = version;
      })
      .catch((err) => {
        console.warn('[Sync] failed to load server sync state:', err);
      });
    // Through the tracker as well as the React state: this runs on a user
    // change, when the previous session may have left the flag `true` (see
    // `onDispose`). 'connecting' is not 'ready', and an edit made before the
    // new socket is up has to be remembered.
    getUnsyncedDeckTracker().setSyncReady(false);
    setPersonalSyncReady(false);
    setSyncStatus('connecting');
  }, [userId]);

  const ctx: SyncCtx = useMemo(
    () => ({
      serverTsRef,
      serverVersionRef,
      serverTsLoadedRef,
      sendRef,
      sendCompressedRef,
      hasCompletedInitialSyncRef,
      // The unsynced-deck tracker mirrors the status: a card edit made while
      // the socket is not `ready` is remembered for the reconnect flush
      // (`sync-bookkeeping/unsynced-decks.ts`). Set synchronously here — the
      // adapter flips the status right before the flush that depends on it.
      setSyncStatus: (status) => {
        getUnsyncedDeckTracker().setSyncReady(status === 'ready');
        setPersonalSyncStatus(status);
        setSyncStatus(status);
      },
      saveServerSyncedTs,
      saveServerVersion,
    }),
    [],
  );

  const adapter = useMemo(
    () =>
      createUserDoAdapter({
        userId: userId ?? '',
        ctx,
        setActivityData,
        syncStatusRef,
        legacyClient,
        onSeedEvent,
      }),
    [userId, ctx, legacyClient, onSeedEvent],
  );

  useDoSync({
    provider,
    scopeLabel: 'user-do',
    roomId: userId ?? null,
    adapter,
  });

  // Identity-stable result: this object IS `SyncContext.Provider`'s value
  // (sync-core renders `value={sync}`), and the provider re-renders on every
  // auth-context tick above it (Clerk re-publishes on each token mint). A
  // fresh literal here turned each of those ticks into a value-equal but
  // identity-new context publish — re-rendering every `useSync()` consumer
  // app-wide for a state that had not changed (measured mid deck-open via
  // WDYR, 2026-08-27).
  return useMemo(
    () => ({
      syncStatus,
      seed,
      isOnline,
      activityData,
      hasCompletedInitialSync,
    }),
    [syncStatus, seed, isOnline, activityData, hasCompletedInitialSync],
  );
}
